import React, { useEffect, useState, useMemo, useRef } from 'react'; 
import PropTypes from 'prop-types';
import styles from './TraderDashboard.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { useAICopilot } from '../../hooks/useAICopilot';

// Sub-components
import ExchangeSelector from './ExchangeSelector';
import TickerInput from './TickerInput';
import DateInputs from './DateInputs';
import FinancialParams from './FinancialParams';
import AdvancedParams from './AdvancedParams';
import ActionButtons from './ActionButtons';
import ErrorMessage from './ErrorMessage';
import CompactAICopilot from './CompactAICopilot';

function BacktestForm({ 
  params, onInputChange, onIndexChange, isJobRunning, 
  isJobCompleted, onSubmit, onCancel, onDownload, canDownload, localError 
}) {
  const [tickerCount, setTickerCount] = useState(0);
  const [showAIPanel, setShowAIPanel] = useState(false);
  
  // Track render counts and reasons for production debugging
  const renderCount = useRef(0);
  renderCount.current++;

  const { 
    analysis, 
    loading: aiLoading, 
    error: aiError, 
    healthStatus,
    analyzeStrategyEnhanced,
    askQuestion,
    clearAnalysis 
  } = useAICopilot();

  // 1. STABILIZE TICKERS: Create a memoized array from the raw string
  const memoizedTickers = useMemo(() => {
    const tickerArray = params.custom_tickers
      .split(',')
      .map(t => t.trim())
      .filter(t => t !== '');
    
    console.log(`[BacktestForm] 🏷️ Tickers stabilized. Count: ${tickerArray.length}`);
    return tickerArray;
  }, [params.custom_tickers]);

  // 2. STABILIZE STRATEGY PARAMS: Use primitive dependencies only
  const strategyParams = useMemo(() => {
    console.log(`[BacktestForm] 🧠 RCA: Recalculating strategyParams identity (Render #${renderCount.current})`);
    return {
      strategy_name: 'momentum',
      lookback_period: params.lookback_period || 20,
      threshold: params.threshold || 0.05,
      initial_capital: params.initial_capital,
      tickers: memoizedTickers, 
      index_name: params.index_name || 'custom',
      start_date: params.start_date,
      end_date: params.end_date,
      n_top_performers: params.n_top_performers || 5,
      drawdown_threshold: params.drawdown_threshold || 20,
      risk_free_rate: params.risk_free_rate || 0.02
    };
  }, [
    memoizedTickers,
    params.lookback_period,
    params.threshold,
    params.initial_capital,
    params.index_name,
    params.start_date,
    params.end_date,
    params.n_top_performers,
    params.drawdown_threshold,
    params.risk_free_rate
  ]);

  // 3. STABILIZE MARKET DATA
  const marketData = useMemo(() => {
    return {
      tickers: memoizedTickers,
      startDate: params.start_date,
      endDate: params.end_date,
      marketSegment: params.index_name
    };
  }, [memoizedTickers, params.start_date, params.end_date, params.index_name]);

  useEffect(() => {
    setTickerCount(memoizedTickers.length);
  }, [memoizedTickers]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (analysis) clearAnalysis();
    onSubmit(e);
  };

  return (
    <div className={`${styles.backTestFormCard} ${styles.formContainer}`}>
      <form onSubmit={handleFormSubmit}>
        <div className={`${styles.twoColumnRow}`}>
          <ExchangeSelector 
            currentIndex={params.index_name}
            onIndexChange={onIndexChange}
          />
          <div className={styles.buttonColumn}>
            <div className={styles.actionButtons}>
              {healthStatus?.enabled && (
                <button 
                  type="button"
                  onClick={() => {
                    if (isJobRunning) return;
                    setShowAIPanel(!showAIPanel);
                  }}
                  className={`${styles.button} ${showAIPanel ? styles.aiButtonActive : styles.aiButton} ${isJobRunning ? styles.buttonDisabled : ''}`}
                  disabled={isJobRunning}
                  title={isJobRunning ? "Focus on backtest in progress" : "Toggle AI Copilot"}
                >
                  <FontAwesomeIcon icon={faLightbulb} />
                  <span>AI Copilot {showAIPanel ? '▲' : '▼'}</span>
                  {isJobRunning && <span className={styles.buttonBadge}>⏳</span>}
                </button>
              )}
              
              <ActionButtons
                isJobRunning={isJobRunning}
                isJobCompleted={isJobCompleted}
                onCancel={onCancel}
                onDownload={onDownload}
                canDownload={canDownload}
                onSubmit={handleFormSubmit}
                showAIAnalyze={false}
                aiLoading={aiLoading}
                hideRunButton={showAIPanel}
              />
            </div>
          </div>
        </div>

        {showAIPanel && healthStatus?.enabled && (
          <div className={styles.compactAIPanel}>
            <CompactAICopilot
              strategyParams={strategyParams}    
              marketData={marketData}             
              aiError={aiError}
              clearAnalysis={clearAnalysis}
              analysis={analysis}
              aiLoading={aiLoading}
              healthStatus={healthStatus}
              analyzeStrategyEnhanced={analyzeStrategyEnhanced}
              askQuestion={askQuestion}
            />
          </div>
        )}

        <TickerInput 
          value={params.custom_tickers}
          onChange={onInputChange}
          tickerCount={tickerCount}
          healthStatus={healthStatus}
        />

        <DateInputs params={params} onInputChange={onInputChange} />
        <FinancialParams params={params} onInputChange={onInputChange} />
        <AdvancedParams params={params} onInputChange={onInputChange} />

        {localError && <ErrorMessage message={localError} />}
      </form>
    </div>
  );
}

// PropTypes omitted for brevity (same as original)
export default BacktestForm;